import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronRight, ArrowUpRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { getArticleBySlug } from "@/data/articles";

export default function Article() {
  useReveal();
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) return <Navigate to="/blog" replace />;

  const Icon = article.icon;

  return (
    <div data-testid={`page-article-${article.slug}`} className="bg-[#F5F2EB]">
      {/* Banda navy decorativa — separa header dall'articolo */}
      <section
        data-testid="article-top-band"
        className="bv-network text-white"
        aria-hidden="true"
      >
        <div className="bv-container py-10 md:py-12" />
      </section>

      {/* HERO */}
      <section className="bv-container pt-14 md:pt-20 pb-10 md:pb-14" data-testid="article-hero">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          data-testid="article-breadcrumb"
          className="flex items-center flex-wrap gap-1.5 font-mono text-[11px] tracking-[0.18em] uppercase text-[#0E2E4D]/55"
        >
          <Link to="/" className="hover:text-[#2BA8AD] transition-colors">Home</Link>
          <ChevronRight size={12} strokeWidth={2} />
          <Link to="/blog" className="hover:text-[#2BA8AD] transition-colors">Blog</Link>
          <ChevronRight size={12} strokeWidth={2} />
          <span className="text-[#0E2E4D]/80 normal-case tracking-normal font-sans text-[12px] truncate max-w-[60vw] md:max-w-none">
            {article.title}
          </span>
        </nav>

        {/* Category + Date */}
        <div className="mt-10 flex items-center gap-4 reveal">
          <span className="inline-flex items-center gap-2 bg-white px-3 py-1.5 font-mono text-[10px] tracking-[0.22em] uppercase text-[#2BA8AD] border border-[#2BA8AD]/20">
            <Icon size={12} strokeWidth={2} />
            {article.category}
          </span>
          <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#0E2E4D]/55">
            {article.date}
          </span>
        </div>

        {/* Title */}
        <h1
          data-testid="article-title"
          className="h-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-6 max-w-[24ch] leading-[1.05] uppercase text-[#0E2E4D] reveal"
        >
          {article.title}
        </h1>

        {/* Subtitle (excerpt) */}
        <p
          data-testid="article-subtitle"
          className="mt-6 max-w-2xl text-[17px] md:text-[19px] text-[#0E2E4D]/65 leading-relaxed reveal"
        >
          {article.excerpt}
        </p>
      </section>

      {/* COVER IMAGE */}
      <section className="bv-container pb-14 md:pb-20" data-testid="article-cover">
        <div className="aspect-[16/8] overflow-hidden border border-[#0E2E4D]/10 reveal">
          <img
            src={article.cover}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* BODY */}
      <section className="bv-container pb-20 md:pb-28" data-testid="article-body">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8 lg:col-start-3">
            {article.sections.map((s, idx) => (
              <SectionBlock key={idx} section={s} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bv-network text-white" data-testid="article-cta">
        <div className="bv-container py-20 md:py-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-8 reveal">
              <p className="overline" style={{ color: "#36BFC4" }}>/ Consulenza</p>
              <h2 className="h-display text-3xl md:text-5xl mt-6 leading-[1.05] max-w-[28ch]">
                {article.cta}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 flex md:justify-end reveal" style={{ "--reveal-delay": "120ms" }}>
              <Link
                to="/contatti#form"
                data-testid="article-cta-button"
                className="inline-flex items-center gap-3 bg-[#2BA8AD] hover:bg-[#36BFC4] text-white px-8 py-5 text-[12px] tracking-[0.20em] uppercase font-display font-bold transition-colors"
              >
                Richiedi una consulenza <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionBlock({ section, index }) {
  return (
    <div className="mt-12 md:mt-16 first:mt-0 reveal" data-testid={`article-section-${index}`}>
      <p className="font-mono text-[11px] tracking-[0.22em] text-[#2BA8AD]">
        {`/ ${String(index + 1).padStart(2, "0")}`}
      </p>
      <h2 className="h-display text-2xl md:text-3xl mt-3 leading-tight text-[#0E2E4D] uppercase max-w-[28ch]">
        {section.title}
      </h2>
      <div className="mt-3 h-[2px] w-12 bg-[#2BA8AD]" />

      {section.paragraphs?.map((p, i) => (
        <p key={i} className="mt-6 text-[16px] md:text-[17px] text-[#0E2E4D]/80 leading-[1.75]">
          {p}
        </p>
      ))}

      {section.intro && (
        <p className="mt-6 text-[16px] md:text-[17px] text-[#0E2E4D]/80 leading-[1.75]">
          {section.intro}
        </p>
      )}

      {section.list && (
        <ul className="mt-4 space-y-3">
          {section.list.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-[16px] md:text-[17px] text-[#0E2E4D]/80 leading-[1.6]">
              <span
                aria-hidden="true"
                className="mt-2.5 w-2 h-2 shrink-0 rounded-full"
                style={{ backgroundColor: "#2BA8AD" }}
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {section.afterList && (
        <p className="mt-6 text-[16px] md:text-[17px] text-[#0E2E4D]/80 leading-[1.75]">
          {section.afterList}
        </p>
      )}
    </div>
  );
}
